"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "OTA STRATEGY"
}, {
  id: "m-02",
  index: "02",
  label: "TECH STACK"
}, {
  id: "m-03",
  index: "03",
  label: "REVENUE AUDIT"
}, {
  id: "m-04",
  index: "04",
  label: "MARKET DEMAND"
}, {
  id: "m-05",
  index: "05",
  label: "DIRECT BOOKINGS"
}, {
  id: "m-06",
  index: "06",
  label: "PRICING MODELS"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "OTA STRATEGY",
  instructions: ["Optimize property descriptions with high-converting, SEO-friendly keywords.", "Maintain parity across all channels while leveraging dynamic rate adjustments.", "Utilize high-quality imagery to increase click-through rates on major platforms.", "Monitor competitor pricing daily to ensure your property remains competitive.", "Analyze OTA commission structures to maximize net revenue per booking."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_strategy_analysis.png-wvc-sizes",
    alt: "Data visualization showing OTA revenue growth trends",
    tag: "[FIG. 01 / OTA GROWTH]"
  },
  caution: "Avoid rate disparity, as it can lead to penalties from major booking platforms."
}, {
  id: "m-02",
  index: "M-02",
  title: "TECH STACK",
  instructions: ["Integrate a robust Property Management System (PMS) for seamless operations.", "Implement a Channel Manager to prevent overbookings and sync inventory.", "Use Revenue Management Systems (RMS) for AI-driven pricing recommendations.", "Ensure your booking engine is mobile-optimized for frictionless user experience.", "Regularly audit your tech stack to ensure compatibility and data security."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-sizes",
    alt: "Diagram of integrated hotel technology ecosystem",
    tag: "[FIG. 02 / TECH INTEGRATION]"
  },
  caution: "Incompatible software can lead to data silos and operational inefficiencies."
}, {
  id: "m-03",
  index: "M-03",
  title: "REVENUE AUDIT",
  instructions: ["Conduct quarterly audits to identify underperforming revenue streams.", "Review historical booking data to forecast future demand patterns accurately.", "Analyze guest feedback to improve service quality and increase ADR.", "Evaluate marketing spend against direct booking conversion rates.", "Benchmark your property performance against local market competitors."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_process.webp-wvc-sizes",
    alt: "Revenue audit report showing performance metrics",
    tag: "[FIG. 03 / AUDIT CYCLE]"
  },
  caution: "Ignoring audit findings can result in missed revenue opportunities and market share loss."
}, {
  id: "m-04",
  index: "M-04",
  title: "MARKET DEMAND",
  instructions: ["Monitor local events and seasonal trends to adjust pricing strategies.", "Use predictive analytics to anticipate shifts in travel demand.", "Adjust inventory availability based on high-demand periods.", "Leverage social media trends to capture emerging traveler segments.", "Collaborate with local tourism boards to boost destination visibility."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_demand_forecasting.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_demand_forecasting.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/market_demand_forecasting.png-wvc-sizes",
    alt: "Market demand forecasting chart for hospitality",
    tag: "[FIG. 04 / DEMAND MAP]"
  },
  caution: "Failure to react to market shifts quickly can lead to significant revenue loss."
}, {
  id: "m-05",
  index: "M-05",
  title: "DIRECT BOOKINGS",
  instructions: ["Offer exclusive perks for guests booking directly through your website.", "Implement a loyalty program to encourage repeat business and direct engagement.", "Optimize your website for local SEO to capture organic search traffic.", "Use retargeting ads to convert visitors who abandoned the booking process.", "Ensure the booking process is simple, secure, and fast."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_conversion.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_conversion.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/direct_booking_conversion.png-wvc-sizes",
    alt: "Conversion funnel for direct hotel bookings",
    tag: "[FIG. 05 / DIRECT SALES]"
  },
  caution: "High reliance on OTAs reduces profit margins; prioritize direct channels."
}, {
  id: "m-06",
  index: "M-06",
  title: "PRICING MODELS",
  instructions: ["Adopt dynamic pricing based on real-time occupancy and demand.", "Implement length-of-stay restrictions during peak demand periods.", "Use tiered pricing to cater to different guest segments.", "Offer package deals that bundle room rates with local experiences.", "Review pricing strategies weekly to stay ahead of market fluctuations."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_model.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_model.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/dynamic_pricing_model.png-wvc-sizes",
    alt: "Dynamic pricing model visualization",
    tag: "[FIG. 06 / PRICING STRATEGY]"
  },
  caution: "Static pricing models are obsolete in today's fast-paced hospitality market."
}];
export default function TechnicalFAQ() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="technicalfaq" className="bg-background py-28 md:py-40">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-secondary" aria-hidden="true" />
            <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Revenue Strategy Guide
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-black uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              The{" "}
              <em className="not-italic md:italic font-black" style={{
              color: "var(--secondary)",
              fontStyle: "italic"
            }}>
                Expert's
              </em>{" "}
              Revenue Management Blueprint.
            </h1>
            <div className="shrink-0">
              <span className="inline-block font-default text-xs font-semibold uppercase tracking-[0.12em] text-foreground border border-foreground px-3 py-1.5">
                [REV. 2025.03 / STRATEGY]
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.85
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-default text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-secondary bg-secondary text-secondary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-default text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-secondary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-secondary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-default text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-secondary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-default text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-secondary text-secondary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-default text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-secondary" aria-hidden="true" />
                            Strategy
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-default text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-default text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--secondary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-default text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}